import React, { useMemo } from "react";
import {
  getWorker360Shifts,
  getWorker360ShiftStart,
  normalizeWorker360Value,
  worker360ToDate,
} from "../utils/worker360Metrics";

function titleCase(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}

function formatDateTime(value) {
  const date = worker360ToDate(value);

  if (!date) return "Date unavailable";

  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getTone(type) {
  const tones = {
    registration: {
      color: "#1d4ed8",
      background: "#dbeafe",
    },
    compliance: {
      color: "#7c3aed",
      background: "#ede9fe",
    },
    shift: {
      color: "#166534",
      background: "#dcfce7",
    },
    payroll: {
      color: "#b45309",
      background: "#fef3c7",
    },
    warning: {
      color: "#b91c1c",
      background: "#fee2e2",
    },
    profile: {
      color: "#475569",
      background: "#e2e8f0",
    },
  };

  return tones[type] || tones.profile;
}

export default function Worker360Activity({
  worker,
  shifts = [],
}) {

  const timeline = useMemo(() => {
    if (!worker) return [];

    const events = [];

    function addEvent({
      id,
      date,
      title,
      description,
      type = "profile",
    }) {
      const parsedDate =worker360ToDate( date);

      if (!parsedDate) return;

      events.push({
        id,
        date: parsedDate,
        title,
        description,
        type,
      });
    }

    addEvent({
      id: "account-created",
      date: worker.createdAt,
      title: "Worker Account Created",
      description:
        worker.email || "Worker profile entered the platform.",
      type: "registration",
    });

    addEvent({
      id: "registration-approved",
      date:
        worker.registrationApprovedAt ||
        worker.approvedAt,
      title: "Registration Approved",
      description: "Worker was approved to begin onboarding.",
      type: "registration",
    });

    addEvent({
      id: "onboarding-submitted",
      date:
        worker.onboardingSubmittedAt ||
        worker.submittedAt,
      title: "Onboarding Submitted",
      description:
        "Worker submitted onboarding information for review.",
      type: "compliance",
    });

    addEvent({
      id: "compliance-approved",
      date:
        worker.complianceApprovedAt ||
        worker.credentialsApprovedAt ||
        worker.onboardingApprovedAt,
      title: "Compliance Approved",
      description:
        "Required credentials and documents were approved.",
      type: "compliance",
    });

    addEvent({
      id: "worker-ready",
      date:
        worker.readyAt ||
        worker.workerApprovedAt ||
        worker.onboardingCompletedAt,
      title: "Ready for Shifts",
      description:
        "Worker became operationally available for scheduling.",
      type: "compliance",
    });

    addEvent({
      id: "facility-transfer",
      date:
        worker.facilityTransferredAt ||
        worker.lastFacilityChangeAt,
      title: "Facility Assignment Updated",
      description: `Current facility: ${
        worker.facilityName ||
        worker.facilityId ||
        "Not assigned"
      }.`,
      type: "profile",
    });

    addEvent({
      id: "suspended",
      date: worker.suspendedAt,
      title: "Worker Suspended",
      description:
        worker.suspensionReason ||
        "Worker access or scheduling was suspended.",
      type: "warning",
    });

const workerShifts = getWorker360Shifts(
  worker,
  shifts
);

    workerShifts.forEach((shift, index) => {
      const shiftId = shift.id || shift.shiftId || index;
      const status = normalizeWorker360Value(shift.status);
      const facility =
        shift.facilityName ||
        shift.facilityId ||
        "Facility";

      addEvent({
        id: `shift-${shiftId}`,
        date:
          shift.completedAt ||
          shift.arrivedAt ||
          shift.activatedAt ||
          shift.assignedAt ||
          shift.updatedAt ||
          getWorker360ShiftStart(shift),
        title: `Shift ${titleCase(status || "updated")}`,
        description: `${facility} · ${
          shift.position ||
          shift.primaryRole ||
          shift.role ||
          worker.primaryRole ||
          worker.position ||
          "Worker"
        }`,
        type:
          status === "no_show" ||
          status === "cancelled" ||
          status === "canceled"
            ? "warning"
            : "shift",
      });

      const payrollStatus =
  normalizeWorker360Value(shift.payrollStatus);

      if (payrollStatus) {
        addEvent({
          id: `payroll-${shiftId}`,
          date:
            shift.payrollUpdatedAt ||
            shift.paidAt ||
            shift.updatedAt ||
            shift.completedAt,
          title: `Payroll ${titleCase(payrollStatus)}`,
          description: `Payroll status updated for the ${facility} shift.`,
          type: "payroll",
        });
      }
    });

    return events.sort(
      (a, b) => b.date.getTime() - a.date.getTime()
    );
  }, [worker, shifts]);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div>
        <div
          style={{
            color: "#0f172a",
            fontSize: 20,
            fontWeight: 950,
          }}
        >
          Activity Timeline
        </div>

        <div
          style={{
            marginTop: 4,
            color: "#64748b",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          Registration, compliance, scheduling, and payroll activity.
        </div>
      </div>

      <div
        style={{
          padding: 13,
          borderRadius: 14,
          border: "1px solid #e2e8f0",
          background: "#f8fafc",
        }}
      >
        <div
          style={{
            color: "#64748b",
            fontSize: 10,
            fontWeight: 900,
            textTransform: "uppercase",
          }}
        >
          Derived Events
        </div>

        <div
          style={{
            marginTop: 5,
            color: "#0f172a",
            fontSize: 22,
            fontWeight: 950,
          }}
        >
          {timeline.length}
        </div>
      </div>

      {timeline.length === 0 ? (
        <div
          style={{
            padding: 20,
            borderRadius: 14,
            textAlign: "center",
            color: "#64748b",
            background: "#f8fafc",
            border: "1px dashed #cbd5e1",
            fontWeight: 750,
          }}
        >
          No dated activity is available for this worker.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {timeline.map((event) => {
            const tone = getTone(event.type);

            return (
              <div
                key={event.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "36px 1fr",
                  gap: 10,
                  padding: 13,
                  borderRadius: 14,
                  border: "1px solid #e2e8f0",
                  background: "#ffffff",
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: 999,
                    color: tone.color,
                    background: tone.background,
                    fontSize: 14,
                    fontWeight: 950,
                  }}
                >
                  •
                </div>

                <div>
                  <div
                    style={{
                      color: "#0f172a",
                      fontSize: 13,
                      fontWeight: 950,
                    }}
                  >
                    {event.title}
                  </div>

                  <div
                    style={{
                      marginTop: 3,
                      color: "#64748b",
                      fontSize: 11,
                      fontWeight: 750,
                    }}
                  >
                    {formatDateTime(event.date)}
                  </div>

                  <div
                    style={{
                      marginTop: 6,
                      color: "#475569",
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1.45,
                    }}
                  >
                    {event.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}